import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import SelectMenu from '../../component/common/SelectMenu';
import {
  StandardButton,
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  distributionBarOptions,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X,
  paletteAt
} from '../../component/common/designSystem';
import {
  HABIT_FREQUENCY_COLORS,
  HABIT_SOURCE_COLORS,
  HABIT_STATUS_COLORS,
  HABIT_OUTCOME_COLORS,
  STREAK_RAMP,
  ADHERENCE_RAMP,
  HABITS_PER_USER_RAMP,
  CONSISTENCY_RAMP,
  TIME_OF_DAY_RAMP
} from './habitsTheme';
import { fetchHabitsDashboard, formatBigNumber, lastNDaysLabels } from './habitsHubApi';

// '' = all users; the value is passed straight through to the API's scope param.
const SCOPES = [
  { name: 'All', value: '' },
  { name: 'B2C', value: 'B2C' },
  { name: 'B2B', value: 'B2B' }
];

/**
 * Habit Tracker → Tracker Dashboard.
 *
 * Reads as five bands, the same shape as the Sleep and Breathwork trackers:
 *  1. Headline stats — the tracking population and how well it is doing.
 *  2. Distributions — streaks, adherence, load per person, consistency,
 *     lifecycle, source, frequency and what happened to logged activity.
 *  3. Trends over the last 30 days — completions and new adoptions.
 *  4. Patterns — the headline outcome, and which weekdays people follow through.
 *  5. Timing — when habits are scheduled vs when they actually get done.
 *
 * Reused by the company panel via the `fetcher` prop (company-scoped data);
 * `populationLabel` names the scoped population (e.g. "employees"). When a
 * fetcher is supplied the All/B2C/B2B picker and the content-management
 * shortcut are hidden — a company admin has neither the choice nor the access.
 */
const TrackerDashboard = ({ fetcher = null, populationLabel = 'users' }) => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [scope, setScope] = useState('');
  const [stats, setStats] = useState(null);

  const isCompanyScoped = Boolean(fetcher);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    const request = fetcher ? fetcher() : fetchHabitsDashboard(scope);
    request.then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [scope, fetcher]);

  const completionsPerDay = stats?.completionsPerDay || [];
  const newHabitsPerDay = stats?.newHabitsPerDay || [];
  const weekday = stats?.weekdayPattern || [];
  const topHabits = stats?.topHabits || [];
  const statusDistribution = stats?.statusDistribution || [];
  const sourceSplit = stats?.sourceSplit || [];
  const frequencySplit = stats?.frequencySplit || [];
  const outcomeSplit = stats?.outcomeSplit || [];
  const streaks = stats?.streakDistribution || [];
  const adherence = stats?.adherenceDistribution || [];
  const habitsPerUser = stats?.habitsPerUserDistribution || [];
  const consistency = stats?.trackingConsistency || [];
  const scheduleTime = stats?.scheduleTimeDistribution || [];
  const completionTime = stats?.timeOfDayDistribution || [];

  const trendLabels = lastNDaysLabels(completionsPerDay.length);

  // Company view only: what share of the population tracks a habit at all.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.usersTracking / stats.totalUsers) * 100) : null;

  // Habits that were adopted and then let go — the churn behind the headline.
  const inactiveHabits = Math.max(
    (stats?.totalUserHabits || 0) - (stats?.activeUserHabits || 0),
    0
  );

  const completionsOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: trendLabels, tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} completions` } }
  };

  const newHabitsOptions = {
    chart: { ...CHART_BASE, type: 'area' },
    colors: [CHART_COLORS.teal],
    stroke: { curve: 'smooth', width: 2 },
    fill: AREA_FILL,
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: lastNDaysLabels(newHabitsPerDay.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} habits started` } }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekday.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} completions` } }
  };

  // Ranked list, so each habit takes its colour from the shared palette.
  const topHabitsOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: topHabits.map((habit, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: topHabits.map((habit) => habit.name) },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} ${populationLabel}` } }
  };

  const scheduleOptions = distributionBarOptions({
    labels: scheduleTime.map((band) => band.label),
    colors: TIME_OF_DAY_RAMP,
    unit: 'habits'
  });

  const completionTimeOptions = distributionBarOptions({
    labels: completionTime.map((band) => band.label),
    colors: TIME_OF_DAY_RAMP,
    unit: 'completions'
  });

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Habit Tracker Dashboard | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          {isCompanyScoped ? (
            <span className="text-sm font-medium text-shoorah-btn dark:text-white capitalize">
              {populationLabel}
            </span>
          ) : (
            <>
              <span className="text-sm font-medium text-shoorah-btn dark:text-white">Users</span>
              <div className="w-40">
                <SelectMenu
                  menuList={SCOPES}
                  showLabel={false}
                  defaultSelected={SCOPES.find((entry) => entry.value === scope)}
                  setSelectedMenu={(entry) => setScope(entry.value)}
                />
              </div>
            </>
          )}
        </div>
        {isCompanyScoped ? null : (
          <StandardButton
            text="Manage habits"
            variant="secondary"
            onClick={() => navigate('/habit-tracker/manage')}
          />
        )}
      </div>

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title={`${populationLabel === 'users' ? 'Users' : 'People'} Tracking`}
          value={stats ? formatBigNumber(stats.usersTracking) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title="Active Habits"
          value={stats ? formatBigNumber(stats.activeUserHabits) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Completions (30d)"
          value={stats ? formatBigNumber(stats.completions30d) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Completion Rate"
          value={stats ? `${stats.completionRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Avg Current Streak"
          value={stats ? `${stats.avgCurrentStreak} days` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.personalBest}
          title="Best Streak"
          value={stats ? `${stats.bestStreak} days` : '-'}
        />
      </div>

      {/* ---------- Distribution summary ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <MiniDonutCard
          title="Current Streaks"
          subtitle="Consecutive days, active habits"
          data={streaks}
          colors={STREAK_RAMP}
          unit="habits"
        />
        <MiniDonutCard
          title="Habit Adherence"
          subtitle="Lifetime completions per habit"
          data={adherence}
          colors={ADHERENCE_RAMP}
          unit="habits"
        />
        <MiniDonutCard
          title="Habits per Person"
          subtitle="How much people take on"
          data={habitsPerUser}
          colors={HABITS_PER_USER_RAMP}
          unit={populationLabel}
        />
        <MiniDonutCard
          title="Tracking Consistency"
          subtitle="Days completed in 30 days"
          data={consistency}
          colors={CONSISTENCY_RAMP}
          unit={populationLabel}
        />
        <MiniDonutCard
          title="Habit Status"
          subtitle="Lifecycle state"
          data={statusDistribution}
          colors={HABIT_STATUS_COLORS}
          unit="habits"
        />
        <MiniDonutCard
          title="Habit Source"
          subtitle="Shoorah catalog vs custom"
          data={sourceSplit}
          colors={HABIT_SOURCE_COLORS}
          unit="habits"
          legendLimit={2}
        />
        <MiniDonutCard
          title="Frequency"
          subtitle="How habits are scheduled"
          data={frequencySplit}
          colors={HABIT_FREQUENCY_COLORS}
          unit="habits"
        />
        <MiniDonutCard
          title="Logged Outcomes"
          subtitle="What happened when due"
          data={outcomeSplit}
          colors={HABIT_OUTCOME_COLORS}
          unit="activities"
        />
      </div>

      {/* ---------- Trends ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Completions Per Day" subtitle="All habit completions — last 30 days">
          {completionsPerDay.some((value) => value > 0) ? (
            <Chart
              type="bar"
              height={340}
              width="100%"
              series={[{ name: 'Completions', data: completionsPerDay }]}
              options={completionsOptions}
            />
          ) : (
            <PanelEmpty>No completions recorded yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="New Habits Started" subtitle="Habits adopted — last 30 days">
          {newHabitsPerDay.some((value) => value > 0) ? (
            <Chart
              type="area"
              height={340}
              width="100%"
              series={[{ name: 'Habits started', data: newHabitsPerDay }]}
              options={newHabitsOptions}
            />
          ) : (
            <PanelEmpty>No habits started yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Patterns ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {reachPercentage != null ? (
          <BigStatPanel
            title="Tracking Reach"
            subtitle={`Share of ${populationLabel} running a habit`}
            value={`${reachPercentage}%`}
            caption={
              stats
                ? `${formatBigNumber(stats.usersTracking)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${populationLabel}`
                : null
            }
          />
        ) : (
          <BigStatPanel
            title="Habits That Stuck"
            subtitle="Adopted habits completed at least once"
            value={stats ? `${stats.stickPercentage}%` : '-'}
            caption={
              stats
                ? `${formatBigNumber(
                  stats.totalUserHabits - stats.neverCompletedHabits
                )} of ${formatBigNumber(stats.totalUserHabits)} habits`
                : null
            }
          />
        )}

        <Panel
          className="lg:col-span-2"
          title="Completions by Weekday"
          subtitle="Which days people follow through"
        >
          {weekday.some((day) => day.completions > 0) ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Completions', data: weekday.map((day) => day.completions) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No completions recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Timing ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/*
          Intention and reality, side by side: the gap between when a habit is
          scheduled and when it actually gets done is the reason a reminder
          time is worth changing.
        */}
        <Panel title="Scheduled For" subtitle="When habits are set to happen">
          {scheduleTime.some((band) => band.count > 0) ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Habits', data: scheduleTime.map((band) => band.count) }]}
              options={scheduleOptions}
            />
          ) : (
            <PanelEmpty>No scheduled times set yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Actually Completed" subtitle="When they really get done">
          {completionTime.some((band) => band.count > 0) ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Completions', data: completionTime.map((band) => band.count) }]}
              options={completionTimeOptions}
            />
          ) : (
            <PanelEmpty>No completions recorded yet.</PanelEmpty>
          )}
        </Panel>

        {/*
          Follow-through health, not habit count: the numbers that matter are
          how many habits went stale and how many have no reminder to catch them.
        */}
        <Panel title="Follow-through Health" subtitle="Where habits are being lost">
          <ProgressList
            items={[
              { label: 'Active', count: stats?.activeUserHabits || 0 },
              { label: 'Paused, completed or archived', count: inactiveHabits },
              { label: 'Never completed once', count: stats?.neverCompletedHabits || 0 },
              { label: 'No reminder set', count: stats?.remindersOff || 0 }
            ]}
            unit="habits"
          />
        </Panel>
      </div>

      {/* ---------- Content ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Top Habits" subtitle="Most adopted, by tracking people">
          {topHabits.length > 0 ? (
            <Chart
              type="bar"
              height={380}
              width="100%"
              series={[{ name: 'People', data: topHabits.map((habit) => habit.users) }]}
              options={topHabitsOptions}
            />
          ) : (
            <PanelEmpty>No habit activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Top Habits Breakdown" subtitle="Adoption, active runners and completions">
          {topHabits.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">#</th>
                  <th className="py-2 pr-4 font-medium">Habit</th>
                  <th className="py-2 pr-4 font-medium text-right">People</th>
                  <th className="py-2 pr-4 font-medium text-right">Still active</th>
                  <th className="py-2 font-medium text-right">Completions</th>
                </>
              }
            >
              {topHabits.map((habit, index) => (
                <tr key={habit.id || index} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">
                    {/* Rank swatch ties each row to its bar in the chart. */}
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: paletteAt(index) }}
                      />
                      {index + 1}
                    </span>
                  </td>
                  <td className="py-2 pr-4">{habit.name}</td>
                  <td className="py-2 pr-4 text-right">{habit.users.toLocaleString()}</td>
                  <td className="py-2 pr-4 text-right">{habit.activeUsers ?? '-'}</td>
                  <td className="py-2 text-right">{habit.completions.toLocaleString()}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No habit activity yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {stats?.commonCompletionTime && (
        <p className="mt-3 text-xs text-shoorah-gray3">
          Most habits get completed in the {stats.commonCompletionTime.toLowerCase()}.
        </p>
      )}
    </div>
  );
};

TrackerDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default TrackerDashboard;
